import { DEFAULT_TIMEZONE, formatRegions, normalizeSiteLanguage } from '@yoyoclub/shared';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getLocalizedTitle } from '../../utils/activityTransform';

interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  slug: string;
  category: string;
  location: string;
  locationDetails: string;
  region?: string[];
  ageMin: number;
  ageMax: number;
  price: number;
  adultPrice: number;
  childRequired?: boolean;
  maxParticipants: number;
  duration: number;
  status: string;
  host: {
    displayName: string;
    id: string;
  };
  images: Array<{
    imageUrl: string;
    order: number;
    isCover: boolean;
  }>;
  sessions: Array<{
    id: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    currentBookings: number;
    price?: number | null;
    adultPrice?: number | null;
  }>;
}

interface SessionEntry {
  activity: Activity;
  startTime: string;
  endTime: string;
  effectiveChildPriceCents: number;
  effectiveAdultPriceCents: number;
  spotsLeft: number;
  dateKey: string;
  startTimestamp: number;
}

interface Props {
  activities: Activity[];
}

const PT_DATE_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: DEFAULT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// YYYY-MM-DD in DEFAULT_TIMEZONE for an ISO timestamp
function getDateKeyInTZ(iso: string): string {
  return PT_DATE_KEY_FORMATTER.format(new Date(iso));
}

// YYYY-MM-DD from a Date object's local components (used for calendar grid cells)
function getLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default function ActivityCalendarView({ activities }: Props) {
  const { t, i18n } = useTranslation(['activities', 'common']);
  const navigate = useNavigate();
  const locale = i18n.language;

  // Today expressed as YYYY-MM-DD in the activity timezone (PT)
  const todayKey = useMemo(() => PT_DATE_KEY_FORMATTER.format(new Date()), []);

  // Flatten all sessions (today + future), enriched with the activity reference
  const sessions = useMemo<SessionEntry[]>(() => {
    const result: SessionEntry[] = [];
    for (const activity of activities) {
      for (const s of activity.sessions) {
        const dateKey = getDateKeyInTZ(s.startTime);
        if (dateKey < todayKey) continue;
        result.push({
          activity,
          startTime: s.startTime,
          endTime: s.endTime,
          effectiveChildPriceCents: s.price ?? activity.price,
          effectiveAdultPriceCents: s.adultPrice ?? activity.adultPrice,
          spotsLeft: s.maxCapacity - s.currentBookings,
          dateKey,
          startTimestamp: new Date(s.startTime).getTime(),
        });
      }
    }
    return result.sort((a, b) => a.startTimestamp - b.startTimestamp);
  }, [activities, todayKey]);

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, SessionEntry[]>();
    for (const s of sessions) {
      const bucket = map.get(s.dateKey);
      if (bucket) bucket.push(s);
      else map.set(s.dateKey, [s]);
    }
    return map;
  }, [sessions]);

  // First session date — drives initial selection & default month
  const firstSessionKey = sessions[0]?.dateKey;

  const initialMonthDate = useMemo(() => {
    const seedKey = firstSessionKey ?? todayKey;
    const seed = parseDateKey(seedKey);
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  }, [firstSessionKey, todayKey]);

  const [currentMonth, setCurrentMonth] = useState(initialMonthDate);
  const [selectedDate, setSelectedDate] = useState<string | null>(firstSessionKey ?? null);

  const findFirstActivityDateInMonth = useCallback(
    (year: number, month: number): string | null => {
      const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
      let earliest: string | null = null;
      for (const key of sessionsByDate.keys()) {
        if (!key.startsWith(prefix)) continue;
        if (key < todayKey) continue;
        if (earliest === null || key < earliest) earliest = key;
      }
      return earliest;
    },
    [sessionsByDate, todayKey]
  );

  const navigateMonth = useCallback(
    (delta: number) => {
      const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1);
      setCurrentMonth(newMonth);
      const firstInMonth = findFirstActivityDateInMonth(
        newMonth.getFullYear(),
        newMonth.getMonth()
      );
      setSelectedDate(firstInMonth);
    },
    [currentMonth, findFirstActivityDateInMonth]
  );

  const jumpToToday = useCallback(() => {
    const today = parseDateKey(todayKey);
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    const firstInMonth = findFirstActivityDateInMonth(today.getFullYear(), today.getMonth());
    // If today's month has no activities, clear selection rather than jumping the right
    // pane to a date that isn't in the currently visible month.
    setSelectedDate(firstInMonth);
  }, [todayKey, findFirstActivityDateInMonth]);

  // Calendar grid days for currentMonth
  const calendarDays = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const days: (Date | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(y, m, d));
    return days;
  }, [currentMonth]);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Date(2024, 0, 7 + i).toLocaleDateString(locale, { weekday: 'short' })
      ),
    [locale]
  );

  const formatMonthYear = (d: Date) =>
    d.toLocaleDateString(locale, { year: 'numeric', month: 'long' });

  const formatSelectedHeading = (key: string) =>
    parseDateKey(key).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: DEFAULT_TIMEZONE,
    });

  const formatDuration = (startIso: string, endIso: string) => {
    const minutes = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
    if (minutes < 60) return `${minutes}m`;
    const rounded = Math.round((minutes / 60) * 10) / 10;
    return `${rounded}h`;
  };

  const selectedDateSessions = selectedDate ? (sessionsByDate.get(selectedDate) ?? []) : [];

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{t('activities:calendar.noUpcomingSessions')}</p>
      </div>
    );
  }

  const renderDots = (count: number, isSelected: boolean) => {
    const display = Math.min(count, 3);
    return (
      <div className="flex gap-0.5 mt-0.5">
        {Array.from({ length: display }, (_, i) => (
          <span
            key={i}
            className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-primary'}`}
          />
        ))}
      </div>
    );
  };

  const calendarBlock = (
    <div className="bg-white rounded-2xl shadow-card p-4 lg:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base lg:text-lg font-bold text-charcoal capitalize">
          {formatMonthYear(currentMonth)}
        </h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={jumpToToday}
            className="px-2.5 py-1 text-tiny font-medium text-gray-600 border border-soft-gray rounded-md hover:bg-warm-white transition-colors"
          >
            {t('activities:calendar.today')}
          </button>
          <button
            type="button"
            onClick={() => navigateMonth(-1)}
            aria-label={t('activities:calendar.previousMonth')}
            className="p-1.5 hover:bg-warm-white rounded-md border border-soft-gray transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => navigateMonth(1)}
            aria-label={t('activities:calendar.nextMonth')}
            className="p-1.5 hover:bg-warm-white rounded-md border border-soft-gray transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day, idx) => (
          <div
            key={`${day}-${idx}`}
            className="text-center text-tiny font-semibold text-gray-500 py-1 uppercase"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, idx) => {
          if (!date) return <div key={`empty-${idx}`} className="aspect-square" />;
          const key = getLocalDateKey(date);
          const dateSessions = sessionsByDate.get(key);
          const hasActivities = !!dateSessions && dateSessions.length > 0;
          const isSelected = selectedDate === key;
          const isToday = key === todayKey;

          let cls =
            'aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-colors ';
          if (isSelected) {
            cls += 'bg-primary text-white shadow-md font-semibold';
          } else if (hasActivities) {
            cls += 'bg-primary-pale text-charcoal font-medium cursor-pointer hover:bg-primary/20';
          } else {
            cls += 'text-gray-400 cursor-not-allowed';
          }
          if (isToday && !isSelected) cls += ' ring-2 ring-primary ring-offset-1';

          return (
            <button
              key={key}
              type="button"
              onClick={() => hasActivities && setSelectedDate(key)}
              disabled={!hasActivities}
              className={cls}
            >
              <span>{date.getDate()}</span>
              {hasActivities && renderDots(dateSessions.length, isSelected)}
            </button>
          );
        })}
      </div>
    </div>
  );

  const activitiesBlock = (
    <div>
      <div className="mb-4 lg:mb-5">
        <h3 className="text-lg lg:text-2xl font-bold text-charcoal capitalize">
          {selectedDate ? formatSelectedHeading(selectedDate) : ''}
        </h3>
      </div>

      {selectedDateSessions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-card p-8 text-center text-gray-500">
          {t('activities:calendar.noActivitiesOnDate')}
        </div>
      ) : (
        <div className="space-y-3 lg:space-y-4">
          {selectedDateSessions.map((entry, idx) => {
            const {
              activity,
              startTime,
              endTime,
              effectiveChildPriceCents,
              effectiveAdultPriceCents,
              spotsLeft,
            } = entry;
            const cover =
              activity.images.find((img) => img.isCover)?.imageUrl ||
              activity.images[0]?.imageUrl ||
              '/placeholder-activity.jpg';
            const title = getLocalizedTitle(activity, locale);
            const ageRange =
              activity.ageMax >= 99
                ? `${activity.ageMin}+`
                : `${activity.ageMin}-${activity.ageMax}`;
            const isAdultOnly = activity.childRequired === false;
            const isSoldOut = spotsLeft <= 0;
            const showLowSpots = !isSoldOut && spotsLeft <= 5;
            const duration = formatDuration(startTime, endTime);
            const priceCents = isAdultOnly ? effectiveAdultPriceCents : effectiveChildPriceCents;
            const priceLabel = isAdultOnly
              ? t('common:pricing.perAdult')
              : t('common:pricing.perChild');

            return (
              <button
                key={`${activity.id}-${idx}`}
                type="button"
                onClick={() => navigate(`/activity/${activity.slug}`)}
                className="group w-full text-left bg-white rounded-xl shadow-card hover:shadow-brand transition-shadow overflow-hidden block"
              >
                <div className="flex items-center gap-2 px-4 py-2.5 bg-primary-pale">
                  <svg
                    className="w-4 h-4 text-primary flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm font-semibold text-primary">
                    {formatTime(startTime)} — {formatTime(endTime)}
                  </span>
                  <span className="text-tiny text-gray-500">· {duration}</span>
                  {isSoldOut ? (
                    <span className="ml-auto bg-gray-500 text-white text-tiny font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      {t('activities:calendar.soldOut')}
                    </span>
                  ) : showLowSpots ? (
                    <span className="ml-auto bg-orange text-white text-tiny font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      {t('common:card.spotsLeft', { count: spotsLeft })}
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-col lg:flex-row">
                  <div className="aspect-[16/9] lg:aspect-[4/3] lg:w-56 lg:flex-shrink-0 lg:self-start overflow-hidden">
                    <img
                      src={cover}
                      alt={title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-1 p-3 lg:p-5">
                    <h4 className="text-sm lg:text-lg font-semibold text-charcoal group-hover:text-primary transition-colors line-clamp-2">
                      {title}
                    </h4>
                    {/* Card meta — match ActivityCard: age + region inline with icons,
                        no host / no locationDetails. */}
                    <div className="flex items-center justify-between mt-2 lg:mt-3">
                      <div className="flex items-center gap-3 flex-wrap text-xs lg:text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-3.5 h-3.5 lg:w-4 lg:h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {t('common:ages.label')} {ageRange}
                        </span>
                        {(() => {
                          const regionLabel = formatRegions(
                            activity.region,
                            normalizeSiteLanguage(locale),
                            'smart'
                          );
                          if (!regionLabel) return null;
                          return (
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              <span className="line-clamp-1">{regionLabel}</span>
                            </span>
                          );
                        })()}
                      </div>
                      <div className="text-base lg:text-lg font-bold text-primary shrink-0">
                        {/* Single session => fixed price, no "starting from" prefix */}$
                        {(priceCents / 100).toFixed(0)}
                        <span className="text-tiny font-normal text-gray-500 ml-0.5">
                          {priceLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 lg:gap-8 items-start">
      <div className="lg:sticky lg:top-6">{calendarBlock}</div>
      <div>{activitiesBlock}</div>
    </div>
  );
}
